import React from 'react';
import { useDocumentSelection } from '../../../hooks/useDocumentSelection';
import { AddBar, RecordSection } from './ProviderDrawerPrimitives';

export type Selection = ReturnType<typeof useDocumentSelection>;

/**
 * One section of a drawer tab, whatever backs it — a document type's uploads or a
 * credential kind's entries. The list only needs to know what is on file, whether the
 * viewer may add one, and how to draw the add control and the cards; so a tab can mix
 * both kinds in one order without caring which is which.
 */
export type SectionItem = {
  key: string;
  /** Section title for the current count ("Medical Licenses"). */
  title: string;
  /** The add-bar button text and the add tooltips ("Medical License"). */
  label: string;
  count: number;
  /** Every clinician is expected to have one: flagged while nothing is on file. */
  required: boolean;
  /** Whether the viewer may add one here (upload the type / add an entry). */
  canAdd: boolean;
  /** The add control pinned to this item: the add-bar button or the title-bar link. */
  renderAdd: (as: 'button' | 'link', content: React.ReactNode) => React.ReactNode;
  /** Document ids the viewer may download here, in display order (the zip order). */
  selectableIds: string[];
  renderCards: (selection: Selection) => React.ReactNode;
  /** Test id of the section; defaults to `<testIdPrefix>-<key>`. */
  testId?: string;
};

type SectionListProps = {
  /** The tab's sections. This list IS the display order of the add bar and the sections. */
  items: readonly SectionItem[];
  /** Selection resets when the drawer moves to another clinician. */
  providerId: string;
  /** Prefix of the add bar (`-add-bar`), empty line (`-empty`), selection and section test ids. */
  testIdPrefix: string;
  /** Who and what the selectable documents are, for the name of a zip of them. */
  zipSubject: string;
  /** Add-bar lead-in when nothing is on file. */
  noneOnFileLead?: string;
  /** Add-bar lead-in when some sections have something and these do not. */
  someOnFileLead?: string;
  /** Shown instead of the bar when nothing is on file and the viewer may not add anything. */
  emptyText?: React.ReactNode;
  /** Dialogs the items' cards open; rendered once, after the sections. */
  children?: React.ReactNode;
};

/**
 * The standard layout of a staff clinician drawer tab's collections
 * (patterns/add-in-context-document-access.md):
 * - a section WITH something on file is a list of cards, "+ Add one" at the right of its
 *   title bar
 * - the sections with NOTHING on file never render: they are shortcut buttons in ONE add
 *   bar (required ones flagged), so nobody is sent to the Documents tab's full dropdown
 *   and no empty sections are rendered
 * - documents the viewer may download carry a checkbox (card: upper left; section title
 *   bar: all of its cards); a selection brings up a floating bar with Select all /
 *   Invert / Deselect and "Download"
 */
export function SectionList({
  items,
  providerId,
  testIdPrefix,
  zipSubject,
  noneOnFileLead = 'Nothing on file yet — add:',
  someOnFileLead = 'Not on file yet — add:',
  emptyText = 'Nothing on file.',
  children,
}: SectionListProps) {
  const onFile = items.filter((item) => item.count > 0);
  // nothing on file: a button when the viewer may add one; a required section the
  // viewer may NOT add to still has to be seen as missing
  const missing = items.filter((item) => item.count === 0 && (item.canAdd || item.required));

  // Selection covers this tab's downloadable documents only, in display order.
  const selection = useDocumentSelection({
    resetKey: `${providerId}|${testIdPrefix}`,
    selectableIds: onFile.flatMap((item) => item.selectableIds),
    zipSubject,
    testIdPrefix,
  });

  return (
    <>
      {missing.length > 0 && (
        <AddBar
          lead={onFile.length > 0 ? someOnFileLead : noneOnFileLead}
          legend={
            missing.some((item) => item.required) && (
              <span className="tw:text-xs tw:text-amber-700">
                <i className="fas fa-triangle-exclamation tw:mr-1" aria-hidden="true" />
                required
              </span>
            )
          }
          data-testid={`${testIdPrefix}-add-bar`}
        >
          {missing.map((item) => {
            // a required section leads with the same hazard icon the tab label shows
            const label = (
              <>
                {item.required ? (
                  <i
                    className="fas fa-fw fa-triangle-exclamation tw:text-amber-700"
                    aria-label="Required"
                  />
                ) : (
                  <i className="fas fa-fw fa-plus" aria-hidden="true" />
                )}{' '}
                {item.label}
              </>
            );
            return (
              <span
                key={item.key}
                data-required={item.required ? true : undefined}
                data-testid={`${testIdPrefix}-missing-${item.key}`}
              >
                {item.canAdd ? (
                  item.renderAdd('button', label)
                ) : (
                  <span className="tw:text-sm tw:text-amber-700">{label}</span>
                )}
              </span>
            );
          })}
        </AddBar>
      )}
      {onFile.length === 0 && missing.length === 0 && (
        <div className="tw:text-sm tw:text-zinc-500" data-testid={`${testIdPrefix}-empty`}>
          {emptyText}
        </div>
      )}
      {onFile.map((item) => (
        <RecordSection
          key={item.key}
          title={item.title}
          count={item.count}
          emptyText={null}
          leading={selection.renderGroupCheckbox(
            item.selectableIds,
            item.title,
            `${testIdPrefix}-select-all-${item.key}`
          )}
          action={
            item.canAdd
              ? item.renderAdd(
                  'link',
                  <>
                    <i className="fas fa-plus tw:mr-1" aria-hidden="true" />
                    Add one
                  </>
                )
              : null
          }
          data-testid={item.testId ?? `${testIdPrefix}-${item.key}`}
        >
          {item.renderCards(selection)}
        </RecordSection>
      ))}
      {selection.bar}
      {children}
    </>
  );
}
